import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  Col,
  Container,
  Row,
  Modal,
  Button as BootstrapButton,
} from "react-bootstrap";
import styled from "styled-components";

import { signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

import Layout from "../components/layout/layout";
import { H1, P3 } from "../components/theme/text/Headings";
import { Button } from "../components/theme/Button";
import {
  Red,
  DarkGrey,
  Black,
  White,
  White08,
} from "../components/theme/colors";

import { useAuth } from "../services/AuthContext";
import RevenueCatService from "../services/RevenueCatService";
import proTag from "../images/ProTag.png";

import { auth, functions } from "../services/firebase.config";

// OPTIMIZED: Import centralized cache system
import dataCache from "../services/DataCache";
import {
  Delete,
  Logout,
  Notifications,
  Settings,
  PrivacyTip,
  Gavel,
  Warning,
  Apartment,
  AutoAwesome,
} from "@mui/icons-material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const ProfileButtonContainer = styled.div`
  padding: 7px 0px;
  background-color: ${DarkGrey};
  border-radius: 10px;
  margin-bottom: 20px;
`;

const ProfileButton = styled(Button)`
  width: 100%;
  background-color: ${DarkGrey};
  border-radius: 0px;
  border: none;
  box-shadow: none;
  text-align: left;
  font-size: 15px;
  color: ${(props) => props.color || White08};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: none;
  outline: none;
  padding: 10px 20px;

  &:hover {
    background-color: ${Black};
    color: ${(props) => props.color || White08};
    box-shadow: none;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    background-color: ${Black};
    color: ${(props) => props.color || White08};
    box-shadow: none;
  }

  &:active {
    background-color: ${Black};
    color: ${(props) => props.color || White08};
    box-shadow: none;
  }
`;

const SettingsButtonContainer = styled.div`
  background-color: ${Black};
  color: ${(props) => props.color || White08};
  height: 30px;
  width: 30px;
  border-radius: 30px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
`;

const ProBadgeImage = styled.img`
  height: 24px;
  width: auto;
  margin-left: 8px;
  vertical-align: middle;
`;

const SettingsOverview = () => {
  const navigate = useNavigate();
  const { currentUserState } = useAuth();

  // Delete account states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  // Pro status state
  const [isProUser, setIsProUser] = useState(false);
  const [checkingProStatus, setCheckingProStatus] = useState(true);

  // Check Pro status on component mount
  useEffect(() => {
    const checkProStatus = async () => {
      if (!currentUserState) {
        setCheckingProStatus(false);
        return;
      }

      try {
        console.log(
          "🔍 Settings: Checking Pro status for user:",
          currentUserState.uid
        );
        const customerInfo = await RevenueCatService.getCustomerInfo();
        const isPro = RevenueCatService.isProFromCustomerInfo(customerInfo);

        console.log("⭐ Settings Pro Check:", {
          userId: currentUserState.uid,
          isPro,
          customerInfo,
          entitlements:
            customerInfo?.entitlements?.active ||
            customerInfo?.customerInfo?.entitlements?.active,
        });

        setIsProUser(isPro);
      } catch (error) {
        console.warn("Error checking Pro status:", error);
        setIsProUser(false);
      } finally {
        // setCheckingProStatus(false); // COMMENTED OUT - Temporarily unused
      }
    };

    checkProStatus();
  }, [currentUserState]);

  const handleSignOut = async () => {
    try {
      // OPTIMIZED: Clear cached data for current user on sign out
      if (currentUserState) {
        dataCache.invalidate(`user-profile-${currentUserState.uid}`);
        dataCache.invalidate(`quiz-completion-*-${currentUserState.uid}`);
        // Clear per-user local storage settings (e.g., notification prefs)
        try {
          localStorage.removeItem(`notif-prefs-${currentUserState.uid}`);
        } catch (_) {}
      }

      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.log("sign out error", error);
    }
  };

  // Comprehensive account deletion function
  const handleDeleteAccount = async () => {
    if (!currentUserState) {
      alert("You need to be logged in to delete your account");
      return;
    }

    setDeletingAccount(true);

    try {
      const userId = currentUserState.uid;

      // Call server-side callable to orchestrate deletion with Admin privileges
      const deleteUserAccount = httpsCallable(functions, "deleteUserAccount");
      await deleteUserAccount({ userId });

      // Clear caches
      dataCache.invalidate(`user-profile-${userId}`);
      dataCache.invalidate(`quiz-completion-*-${userId}`);
      dataCache.invalidate(`shared-quiz-*`);

      // Clear per-user local storage settings (e.g., notification prefs)
      try {
        localStorage.removeItem(`notif-prefs-${userId}`);
      } catch (_) {}

      // Sign out locally (account may already be deleted server-side)
      try {
        await signOut(auth);
      } catch (e) {
        console.warn("Sign out after deletion warning:", e?.message || e);
      }

      setDeletingAccount(false);
      setShowDeleteModal(false);
      navigate("/login");
    } catch (error) {
      setDeletingAccount(false);

      alert(
        "There was an issue deleting your account. Please try again or contact support."
      );
    }
  };

  return (
    <Layout $background={Black}>
      <Container>
        <Row className="justify-content-center">
          <Col style={{ padding: "0px 30px" }}>
            <div className="text-align-center mt-5-real mb-30">
              <H1 color={White} style={{ display: "inline-block", margin: 0 }}>
                Settings
              </H1>
              {isProUser && <ProBadgeImage src={proTag} alt="Pro" />}
            </div>

            <P3 className="mt-10 mb-15" color={White08}>
              <Notifications className="mr-5" />
              Preferences
            </P3>
            <ProfileButtonContainer>
              <ProfileButton
                onClick={() => navigate("/settings-notifications")}
              >
                <SettingsButtonContainer>
                  <Notifications style={{ fontSize: "20px" }} />
                </SettingsButtonContainer>
                Notifications
                <ArrowForwardIosIcon
                  style={{
                    marginLeft: "auto",
                    fontSize: "18px",
                    color: DarkGrey,
                  }}
                />
              </ProfileButton>
              <ProfileButton onClick={() => navigate("/settings-profile")}>
                <SettingsButtonContainer>
                  <Settings style={{ fontSize: "20px" }} />
                </SettingsButtonContainer>
                Profile Settings
                <ArrowForwardIosIcon
                  style={{
                    marginLeft: "auto",
                    fontSize: "18px",
                    color: DarkGrey,
                  }}
                />
              </ProfileButton>

              <ProfileButton onClick={() => navigate("/pro")}>
                <SettingsButtonContainer>
                  <AutoAwesome style={{ fontSize: "20px", color: White08 }} />
                </SettingsButtonContainer>
                {checkingProStatus
                  ? "Vibe Check Pro"
                  : isProUser
                  ? "Manage your Subscription"
                  : "Vibe Check Pro"}
                <ArrowForwardIosIcon
                  style={{
                    marginLeft: "auto",
                    fontSize: "18px",
                    color: DarkGrey,
                  }}
                />
              </ProfileButton>
            </ProfileButtonContainer>

            <P3 className="mt-40 mb-15" color={White08}>
              <Apartment className="mr-5" />
              More
            </P3>
            <ProfileButtonContainer>
              <ProfileButton
                onClick={() =>
                  window.open(
                    "https://www.vibecheckme.com/terms-of-service",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <SettingsButtonContainer>
                  <Gavel style={{ fontSize: "20px" }} />
                </SettingsButtonContainer>
                Terms of Service
                <ArrowForwardIosIcon
                  style={{
                    marginLeft: "auto",
                    fontSize: "18px",
                    color: DarkGrey,
                  }}
                />
              </ProfileButton>
              <ProfileButton
                onClick={() =>
                  window.open(
                    "https://www.vibecheckme.com/privacy-policy",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                <SettingsButtonContainer>
                  <PrivacyTip style={{ fontSize: "20px" }} />
                </SettingsButtonContainer>
                Privacy Policy
                <ArrowForwardIosIcon
                  style={{
                    marginLeft: "auto",
                    fontSize: "18px",
                    color: DarkGrey,
                  }}
                />
              </ProfileButton>
              <ProfileButton onClick={handleSignOut}>
                <SettingsButtonContainer>
                  <Logout style={{ fontSize: "20px" }} />
                </SettingsButtonContainer>
                Sign Out
                <ArrowForwardIosIcon
                  style={{
                    marginLeft: "auto",
                    fontSize: "18px",
                    color: DarkGrey,
                  }}
                />
              </ProfileButton>
            </ProfileButtonContainer>

            <P3 className="mt-40 mb-15" color={White08}>
              <Warning className="mr-5" /> Danger Zone
            </P3>
            <ProfileButtonContainer>
              <ProfileButton
                color={Red}
                onClick={() => setShowDeleteModal(true)}
                disabled={deletingAccount}
              >
                <SettingsButtonContainer>
                  <Delete style={{ fontSize: "20px", color: Red }} />
                </SettingsButtonContainer>
                {deletingAccount ? "Deleting Account..." : "Delete My Account"}
                <ArrowForwardIosIcon
                  style={{
                    marginLeft: "auto",
                    fontSize: "18px",
                    color: DarkGrey,
                  }}
                />
              </ProfileButton>
            </ProfileButtonContainer>
          </Col>
        </Row>
      </Container>

      {/* Delete Account Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => !deletingAccount && setShowDeleteModal(false)}
        centered
        backdrop={deletingAccount ? "static" : true}
        keyboard={!deletingAccount}
      >
        <Modal.Header
          style={{
            backgroundColor: "#e5e5e5",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
            border: "none",
          }}
        >
          <Modal.Title>
            <H1>Delete Account</H1>
          </Modal.Title>
          <button
            type="button"
            className="btn-close"
            onClick={() => !deletingAccount && setShowDeleteModal(false)}
            style={{
              color: DarkGrey,
              filter: "none",
              opacity: 0.8,
              "--bs-btn-close-color": DarkGrey,
              "--bs-btn-close-opacity": 1,
            }}
            disabled={deletingAccount}
          />
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: "#e5e5e5",
            color: DarkGrey,
            border: "none",
          }}
        >
          <p>Are you sure you want to delete your account? This action will:</p>
          <ul>
            <li>Delete your public profile</li>
            <li>Remove your profile photo</li>
            <li>Delete all your quiz results</li>
            <li>Remove all shared quizzes you've created or received</li>
            <li>Permanently delete your account</li>
          </ul>
          <p style={{ color: Red, fontWeight: "bold" }}>
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: "#e5e5e5",
            border: "none",
          }}
        >
          <BootstrapButton
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deletingAccount}
          >
            Cancel
          </BootstrapButton>
          <BootstrapButton
            variant="danger"
            onClick={handleDeleteAccount}
            disabled={deletingAccount}
          >
            {deletingAccount ? "Deleting..." : "Delete Account"}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default SettingsOverview;
